<script>
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { studyProperties, isFeatureEnabled, fetchStudyProperties } from '$stores/configStore';
  import { isCompactMode } from '$stores/uiStore';

  import {
    faMicrochip,
    faClock,
    faDroplet,
    faCalendar,
    faNotdef,
    faNarwhal,
    faHyphen,
  } from '@fortawesome/pro-light-svg-icons';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';

  export let uniqueSensors = new Set();
  export let totalCGMs = 0;
  export let totalValidCGMReadings = 0;
  export let totalInvalidCGMReadings = 0;
  export let totalSMBGs = 0;
  export let totalSMBGReadings = 0;

  // Loading state props
  export let isLoadingSensors = false;
  export let isLoadingCGMDays = false;
  export let isLoadingCGMReadings = false;
  export let isLoadingSMBGDays = false;
  export let isLoadingSMBGReadings = false;

  // Feature flag variables
  let isSMBGEnabled = true;

  // Reactive variables for feature flags
  $: {
    isSMBGEnabled = isFeatureEnabled('smbg');
  }
</script>

<div class="flex">
  <!-- <div class="flex items-center justify-center w-6">
    <span class="text-sm font-semibold text-gray-500 dark:text-gray-400 transform -rotate-90 whitespace-nowrap">Counts</span>
  </div> -->
  <div class="grid grid-cols-5 gap-2 px-1 mt-1 flex-1">
    <!-- Unique CGM Sensors Card -->
    <div
      class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-blue-500"
    >
      <div class="flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faMicrochip} class="text-xl text-blue-500 flex-shrink-0" />
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">CGM Sensors</h3>
      </div>
      {#if isLoadingSensors}
        <p class="text-lg font-bold text-gray-400 dark:text-gray-500 animate-pulse">...</p>
      {:else}
        <p class="text-lg font-bold text-gray-900 dark:text-white">{uniqueSensors.size}</p>
      {/if}
      {#if !$isCompactMode}
        <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">CGM Sensors used</p>
      {/if}
    </div>

    <!-- Days with CGM Data Card -->
    <div
      class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-green-500"
    >
      <div class="flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faCalendar} class="text-xl text-green-500 flex-shrink-0" />
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">CGM Days</h3>
      </div>
      {#if isLoadingCGMDays}
        <p class="text-lg font-bold text-gray-400 dark:text-gray-500 animate-pulse">...</p>
      {:else}
        <p class="text-lg font-bold text-gray-900 dark:text-white">{totalCGMs}</p>
      {/if}
      {#if !$isCompactMode}
        <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Days with CGM readings</p>
      {/if}
    </div>

    <!-- Total CGM Readings Card -->
    <div
      class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-purple-500"
    >
      <div class="flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faDroplet} class="text-xl text-purple-500 flex-shrink-0" />
        <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">CGM Readings</h3>
      </div>
      
      <div class="flex flex-col">
        {#if isLoadingCGMReadings}
          <p class="text-lg font-bold text-gray-400 dark:text-gray-500 animate-pulse">...</p>
          {#if !$isCompactMode}
            <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Valid CGM received</p>
          {/if}
        {:else if totalInvalidCGMReadings > 0}
          <p class="text-lg font-bold text-gray-900 dark:text-white">{totalValidCGMReadings} / {totalInvalidCGMReadings}</p>
          {#if !$isCompactMode}
            <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Valid / Invalid CGM received</p>
          {/if}
        {:else}
          <p class="text-lg font-bold text-gray-900 dark:text-white">{totalValidCGMReadings}</p>
          {#if !$isCompactMode}
            <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Valid CGM received</p>
          {/if}
        {/if}
      </div>
    </div>

    {#if isSMBGEnabled}
      <!-- Days with SMBG Data Card -->
      <div
        class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-orange-500"
      >
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon icon={faCalendar} class="text-xl text-orange-500 flex-shrink-0" />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">SMBG Days</h3>
        </div>
        {#if isLoadingSMBGDays}
          <p class="text-lg font-bold text-gray-400 dark:text-gray-500 animate-pulse">...</p>
        {:else}
          <p class="text-lg font-bold text-gray-900 dark:text-white">{totalSMBGs}</p>
        {/if}
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Days with SMBG readings</p>
        {/if}
      </div>
    {:else}
      <div
        class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-gray-500"
      >
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon icon={faHyphen} class="text-xl text-gray-500 opacity-0 flex-shrink-0" />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">&nbsp;</h3>
        </div>
      </div>
    {/if}
    {#if isSMBGEnabled}
      <!-- Total SMBG Readings Card -->
      <div
        class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-red-500"
      >
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon icon={faDroplet} class="text-xl text-red-500 flex-shrink-0" />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">SMBG Readings</h3>
        </div>
        {#if isLoadingSMBGReadings}
          <p class="text-lg font-bold text-gray-400 dark:text-gray-500 animate-pulse">...</p>
        {:else}
          <p class="text-lg font-bold text-gray-900 dark:text-white">{totalSMBGReadings}</p>
        {/if}
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Total SMBG received</p>
        {/if}
      </div>
    {:else}
      <div
        class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 border-gray-500"
      >
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon icon={faHyphen} class="text-xl text-gray-500 opacity-0 flex-shrink-0" />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">&nbsp;</h3>
        </div>
      </div>
    {/if}
  </div>
</div>
